import {
  Clock, Users, Share2, Bookmark, BookmarkCheck
} from "lucide-react";
import { servingsLabel, type Language, type Recipe } from "../data";
import { getRecipeName } from "../utils/recipe";
import { categoryIcons } from "../data/categories";

interface RecipeCardProps {
  recipe: Recipe & { matchPct?: number };
  lang: Language;
  t: (k: string) => string;
  onView: (r: Recipe) => void;
  isSaved: boolean;
  onSave: () => void;
  onShare: () => void;
  matchPct?: number;
  imageOverride?: string;
}

const FOOD_PLACEHOLDER = "images/recipes/pexels-1640777-600.jpeg";

export function RecipeCard({ recipe, lang, t, onView, isSaved, onSave, onShare, matchPct, imageOverride }: RecipeCardProps) {
  const rawImage = imageOverride ?? recipe.image;
  const imageSrc = rawImage;

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-sm border border-stone-100 dark:border-slate-700 hover:shadow-md dark:hover:shadow-black/30 hover:-translate-y-0.5 transition-all group cursor-pointer"
      onClick={() => onView(recipe)}
    >
      <div className="relative overflow-hidden" style={{ paddingBottom: "133.33%" }}>
        <img
          src={imageSrc}
          alt={getRecipeName(recipe, lang)}
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={e => {
            const img = e.target as HTMLImageElement;
            if (!img.dataset.fallback) {
              img.dataset.fallback = "1";
              img.src = FOOD_PLACEHOLDER;
            }
          }}
        />
      </div>
      <div className="absolute top-3 start-3 z-10">
        <span className="bg-black/55 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
          {categoryIcons[recipe.category]}
          {recipe.category === "pastries" ? t("pastries") : t(recipe.category)}
        </span>
      </div>
      {matchPct !== undefined && (
        <div className={`absolute top-3 end-3 z-10 text-xs px-2.5 py-1 rounded-full font-bold ${matchPct === 100 ? "bg-green-500 text-white" : matchPct >= 75 ? "bg-green-600 text-white" : "bg-amber-400 text-white"}`}>
          {matchPct}%
        </div>
      )}
      {isSaved && matchPct === undefined && (
        <div className="absolute top-3 end-3 z-10 bg-green-600 text-white rounded-full p-1.5">
          <BookmarkCheck className="w-3.5 h-3.5" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/85 via-black/50 to-transparent pt-10 pb-3 px-3">
        <h3 className="font-bold text-white text-sm mb-1 line-clamp-2 drop-shadow leading-tight">
          {getRecipeName(recipe, lang)}
        </h3>
        <div className="flex items-center gap-2 text-xs text-white/80 mb-2.5">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{recipe.time} {t("minutes")}</span>
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{servingsLabel(lang, recipe.servings)}</span>
          <span className="flex items-center gap-1 bg-orange-500/80 backdrop-blur-sm text-white font-bold px-2 py-0.5 rounded-full ms-auto"><span>🔥</span>{recipe.nutrition.calories} kcal</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={e => { e.stopPropagation(); onView(recipe); }}
            className="flex-1 bg-gradient-to-r from-green-600 to-emerald-500 text-white text-xs py-1.5 rounded-xl font-medium hover:from-green-700 hover:to-emerald-600 transition-all"
          >
            {t("viewRecipe")}
          </button>
          <button
            onClick={e => { e.stopPropagation(); onShare(); }}
            className="p-1.5 rounded-xl bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onSave(); }}
            className={`p-1.5 rounded-xl backdrop-blur-sm transition-colors ${isSaved ? "bg-green-500/70 text-white" : "bg-white/20 text-white hover:bg-white/30"}`}
          >
            {isSaved ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
